import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { EntityService } from 'core/api/services/lookUp';
import { FormHelperText } from '@mui/material';
import { CharacterCounter, CurrencyInput, LookUpPriceGroup, NumberInput, RadioGroup, Textarea, TextInput } from '@utriper/core-ui';
import { Container, Label, PRICE_TYPE } from '../../styles';

const DetailsForm = ({ experienceId, form, submitted }) => {
    const { values, errors } = useFormState();
    const priceTypeOptions = [
        {
            id: PRICE_TYPE.INHERIT,
            label: 'Precio Heredado',
        },
        {
            id: PRICE_TYPE.FIXED,
            label: 'Precio Fijo',
        },
        {
            id: PRICE_TYPE.PRICE_GROUP,
            label: 'Grupo de Precios',
        },
    ];

    const priceType = useMemo(() => parseInt(values?.priceType), [values?.priceType]);
    const priceTypeAlert = useMemo(() => {
        let alert = '';
        switch (priceType) {
            case PRICE_TYPE.INHERIT:
                alert = 'Esta opción usará automáticamente el mismo precio que la experiencia.';
                break;
            case PRICE_TYPE.FIXED:
                alert = 'Este precio sobrescribirá el valor original de la experiencia solo para esta opción.';
                break;
            case PRICE_TYPE.PRICE_GROUP:
                alert = 'Este grupo de precios sobrescribirá el valor original de la experiencia solo para esta opción';
                break;
            default:
                break;
        }

        return alert;
    }, [priceType]);

    return (
        <React.Fragment>
            {/* Name */}
            <Container>
                <Label>Titulo de la Opción</Label>
                <Field
                    name="description1"
                    component={TextInput}
                    placeholder="Ej: Experiencia premium"
                    slotProps={{
                        input: {
                            inputProps: {
                                maxLength: 255,
                            },
                        },
                    }}
                />
            </Container>

            {/* Description */}
            <Container>
                <Label>Descripción general (opcional)</Label>
                <Field
                    name="description2"
                    component={Textarea}
                    placeholder="Ej: Una versión más completa de la experiencia original"
                    minRows={4}
                    slotProps={{
                        input: {
                            inputProps: {
                                maxLength: 255,
                            },
                        },
                    }}
                />
                <CharacterCounter length={values?.description2?.length ?? 0} maxLength={255} />
            </Container>

            {/* Duration */}
            <Container>
                <Label>Duración de la Opción</Label>
                <Field name="duration" component={NumberInput} placeholder="Duración aproximada" />
            </Container>

            {/* Price Type */}
            <Container>
                <Label>Tipo de Precio</Label>
                <Field name="priceType" component={RadioGroup} options={priceTypeOptions} row />
            </Container>

            {(priceType === PRICE_TYPE.FIXED || priceType === PRICE_TYPE.PRICE_GROUP) && (
                <Container
                    sx={{
                        '& .MuiAutocomplete-root': { width: '100%' },
                        '& .MuiOutlinedInput-notchedOutline': submitted && (errors?.priceGroupId || errors?.price) ? { borderColor: 'red' } : '',
                    }}
                >
                    <Label>{priceType === PRICE_TYPE.FIXED ? 'Precio' : 'Grupo de Precios'}</Label>
                    {priceType === PRICE_TYPE.FIXED ? (
                        <>
                            <Field
                                name="price"
                                label=" "
                                currency={values.currency}
                                onCurrencyChange={(newCurrency) => {
                                    form.change('currency', newCurrency);
                                }}
                                render={({ input, meta, ...rest }) => (
                                    <CurrencyInput
                                        {...rest}
                                        value={typeof input?.value === 'number' ? input.value : 0}
                                        onChange={(newValue) => {
                                            input.onChange(newValue);
                                        }}
                                    />
                                )}
                            />
                            {submitted && errors?.price && <FormHelperText error>{errors.price}</FormHelperText>}
                        </>
                    ) : (
                        <>
                            <Field
                                name="priceGroupId"
                                render={({ input }) => (
                                    <LookUpPriceGroup
                                        {...input}
                                        label="Busca un grupo de precios"
                                        entityService={{ getList: ({ entity, search, input }) => EntityService.getListByExperienceId({ entity, search, input, experienceId }) }}
                                        value={values?.priceGroupId}
                                        initialValue={values?.priceGroupId}
                                        onChange={(newValue) => {
                                            form.change('priceGroupId', newValue);
                                        }}
                                    />
                                )}
                            />
                            {submitted && errors?.priceGroupId && <FormHelperText error>{errors.priceGroupId}</FormHelperText>}
                        </>
                    )}
                </Container>
            )}

            {/* Price Alert */}
            {priceTypeAlert && <FormHelperText>{priceTypeAlert}</FormHelperText>}

            {submitted && errors?.general && (
                <FormHelperText error sx={{ my: 1 }}>
                    {errors.general}
                </FormHelperText>
            )}
        </React.Fragment>
    );
};

export default DetailsForm;
